/*
 * Copyright (c) 2021 JCA Electronics , Winnipeg, MB. All rights reserved.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import container from '@/app.container';
import { Views } from '@/models/Views';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { Vue, Component } from 'vue-property-decorator';
import { ZenObservable } from 'zen-observable-ts';
import GlobalStore from '@/store/modules/global';
import { getModule } from 'vuex-module-decorators';
import { PubSub } from '@/PubSubClass';

/**
 * Mixin for managing MQTT state and reconnects on inactivity
 */

@Component({
  provide: { container: container }
})
export default class MqttMixIn extends Vue {
  mqttGlobalStore = getModule(GlobalStore, this.$store);
  displayState = this.getState();

  actions: Map<string, () => ZenObservable.Subscription> = new Map();
  subscriptions: Map<string, ZenObservable.Subscription> = new Map();

  mqttFlightPathService: FlightPathServiceInterface | undefined = undefined;

  isWarmingLambdas = false;

  addMqttVisibility(topic: string, action: () => ZenObservable.Subscription) {
    this.actions.set(topic, action);
    this.addMqttVisibilityHandler();
  }

  private addMqttVisibilityHandler() {
    this.clearMqttVisibilityHandler();
    ['pageshow', 'focus', 'blur', 'visibilitychange', 'resume'].forEach(type => {
      window.addEventListener(type, this.onDisplayStateChange, { capture: true });
    });
  }

  removeMqttVisibility() {
    this.subscriptions.forEach(type => {
      type.unsubscribe();
    });
    this.actions.clear();
    this.subscriptions.clear();
    this.clearMqttVisibilityHandler();
  }

  private clearMqttVisibilityHandler() {
    ['pageshow', 'focus', 'blur', 'visibilitychange', 'resume'].forEach(type => {
      window.removeEventListener(type, this.onDisplayStateChange, { capture: true });
    });
  }

  getState() {
    if (document.visibilityState === 'hidden') {
      return 'hidden';
    }
    if (document.hasFocus()) {
      return 'active';
    }
    return 'passive';
  }

  onDisplayStateChange() {
    const prevState = this.displayState;
    const nextState = this.getState();
    if (nextState !== prevState) {
      console.log(`${this.$options.name} : State changed: ${prevState} >>> ${nextState}`);
      this.displayState = nextState;

      if (nextState === 'active') {
        this.subscriptions.forEach((sub, key) => {
          if (sub.closed) {
            const action = this.actions.get(key);
            if (action != undefined) {
              this.subscriptions.set(key, action());
            }
          }
        });

        if (this.$options.name == Views.App) {
          this.warmUpLambdas();
        }
      }
    }
  }

  async warmUpLambdas() {
    if (this.isWarmingLambdas || this.displayState != 'active') return;
    this.isWarmingLambdas = true;
    console.info('Warming lambdas..');
    try {
      // keep flightpath lambda warm, specifically will open a MySQL connection because that first MySQL connection takes a long time on a cold start (~2 seconds)
      await this.mqttFlightPathService?.getAllImplements(this.mqttGlobalStore.accountId);
      // keep alarm lambda warm, intentional bad request - will return empty alarm table
      await this.mqttFlightPathService?.getAlarmTable('warmLambda', '[]');
    } catch (err) {
      console.error(err);
    } finally {
      this.isWarmingLambdas = false;
    }
  }

  mqttError(error: unknown) {
    console.log(error);
    //attempting connection reset
    setTimeout(() => {
      this.subscriptions.forEach(type => {
        type.unsubscribe();
      });
      this.subscriptions.clear();

      PubSub.restartAWSIoTProvider();
      this.actions.forEach((act, key) => {
        this.subscriptions.set(key, act());
      });
    }, 1000 * 10);
  }

  created() {
    this.mqttFlightPathService = container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  async mounted() {
    if (this.mqttFlightPathService != undefined) await this.mqttFlightPathService.updateToken();
  }
}
